import type { IOAuthErrorResponse } from '@manager/api/types/general-types';
import { snackbarMessages } from '@manager/modules/snackbar/snackbar-config';
import { useSnackbarStore } from '@manager/modules/snackbar/snackbar-store';
import type { INotificationOptions } from '@manager/modules/snackbar/snackbar-types';
import {
  createSnackbarItem,
  getDefaultNotificationOptions,
} from '@manager/modules/snackbar/snackbar-utils';
import { i18n } from '@manager/plugins/i18n-plugin';
import {
  getApiError,
  isMigrationBlockedError,
  translateApiErrorCode,
} from '@manager/utils/error-utils';
import { getTenantInvoicingFirstUseBlockedMessage } from '@manager/utils/tenant-invoicing-utils';
import { isAxiosError } from 'axios';
import { uniq } from 'lodash-es';
import type { TranslateResult } from 'vue-i18n';

export interface IUseNotificationsReturn {
  show(text: string, options: INotificationOptions): void;

  showInfo(message?: string | TranslateResult): void;

  showError(message?: string | TranslateResult): void;

  showSuccess(message?: string | TranslateResult): void;

  showErrorResponse(
    response: unknown,
    fallback?: string | TranslateResult,
    time?: number
  ): void;
}

export function useNotifications(): IUseNotificationsReturn {
  const store = useSnackbarStore();

  function show(text: string, options: INotificationOptions) {
    store.queue.push(createSnackbarItem(text, options));
  }

  function showSuccess(message?: string | TranslateResult): void {
    show(
      (message as string) || snackbarMessages.success,
      getDefaultNotificationOptions('success')
    );
  }

  function showInfo(message?: string | TranslateResult): void {
    show(message as string, getDefaultNotificationOptions('info'));
  }

  function showError(message?: string | TranslateResult) {
    show(
      (message as string) || snackbarMessages.error,
      getDefaultNotificationOptions('error')
    );
  }

  function showErrorResponse(
    error?: unknown,
    fallback?: string | TranslateResult
  ) {
    // Already toasted by the http client interceptor.
    if (isMigrationBlockedError(error)) {
      return;
    }

    const firstUseBlockedMessage =
      getTenantInvoicingFirstUseBlockedMessage(error);
    if (firstUseBlockedMessage) {
      showError(firstUseBlockedMessage);
      return;
    }

    const apiError = getApiError(error);

    if (apiError) {
      const { code, message, details } = apiError.error;
      let messages: (string | null)[] = [];

      if (details && !Array.isArray(details)) {
        showError(code);
        return;
      }

      // billing-api pairs its domain not-found code with classic's generic `validation.entity_not_found`.
      const notFoundMessage =
        code.startsWith('domain.') &&
        details?.some((detail) => detail.code === 'validation.entity_not_found')
          ? translateApiErrorCode(code)
          : undefined;
      if (notFoundMessage) {
        showError(notFoundMessage);
        return;
      }

      messages = details?.map((detail) => {
        const namespaced = `apiErrors.${detail.code}`;

        if (i18n.global.te(namespaced)) {
          return i18n.global.t(namespaced, { field: detail.field }) as string;
        }

        // some errors (from Stripe), return the message in the code
        // for these we just want to return the code as it is
        if (!detail.code.startsWith('validation')) {
          return detail.code;
        }

        const translated = i18n.global.t(detail.code, {
          field: detail.field,
        }) as string;

        // vue-i18n returns the key unchanged when missing — fall back to the
        // BE message, then to a generic invalid-value translation.
        if (translated === detail.code) {
          console.warn('No translation found for:', detail.code);
          return (
            detail.message ||
            (i18n.global.t('validation.invalid', {
              field: detail.field,
            }) as string) ||
            null
          );
        }

        return translated;
      });

      if (messages.length > 0 && !messages.some((item) => item == null)) {
        // One refusal can come back on several fields; say it once.
        showError(uniq(messages).join('\n'));
      } else if (code === 'internal.stripe') {
        showError(message || fallback);
      } else {
        showError(translateApiErrorCode(code) ?? fallback);
      }
    } else if (
      isAxiosError(error) &&
      (error.response?.data as IOAuthErrorResponse)?.error?.code ===
        'internal.stripe'
    ) {
      const message = (error.response?.data as IOAuthErrorResponse)?.error
        .message;
      showError(message || fallback);
    } else {
      showError(fallback);
    }
  }

  return {
    show,
    showError,
    showErrorResponse,
    showSuccess,
    showInfo,
  };
}
